import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { mutationErrorMessage } from '../../../lib/mutation-error';
import { formatInZone } from '../../../lib/tripTime';
import {
  audienceKindPhrase,
  classifyPlanError,
  describeExcluded,
  describePlanError,
  describeSequenceCancel,
  detectPlanDateDrift,
  isAfterUnknownOutcome,
  normalizeTripDate,
  planChangedSince,
  planCounts,
  planReschedule,
  RESEND_HINT,
  tripDateLabel,
  type PlanRequest,
} from '../../../lib/reminderPlan';
import { rescheduledSequenceId } from '../../../lib/uuidV5';
import { planName, replacedPlanFlags } from '../../../lib/reminderPlanNaming';
import { plural } from '../../../lib/plural';
import PlanErrorText from './PlanErrorText';
import ScheduledRow from './ScheduledRow';
import { useReminderPlanActions } from './useReminderPlanActions';
import type { GroupDetail, GroupScheduledBroadcast } from '../../../types/groups';

/**
 * A reminder plan on the Scheduled sends list: the rows that share a
 * `sequenceId`, under a header with the plan's counts and one action, "Cancel
 * whole plan" (the sequence DELETE — per-row cancel stays on each row).
 *
 * Also the date-change guard. Each plan's ANCHOR date is the trip start date the API stored on its rows when the plan
 * was made (`sequenceAnchorDate`); a plan without one has no anchor and no guard, because guessing it from the copy or
 * the send times misjudged steps the host had moved on purpose. If the trip's start date moved after the plan was made
 * (anchor != current start date), its pending steps are still aimed at the OLD dates and the plan offers "Reschedule
 * plan": ONE atomic call cancels ALL the pending steps and creates the same steps shifted by the number of days the trip
 * moved (each keeping its own offset and time of day, the date in the copy updated) under a new id derived from the old
 * plan and the new date — so a second tab asking for the same thing changes nothing. What happens to each step: pending steps are
 * cancelled and recreated; a step marked held back, dropped (new time past / too long), or possibly sent (in doubt: attempted, or a
 * worker that stalled) is cancelled and NOT re-created; sent, failed, skipped and cancelled steps are left exactly as they are, and a
 * step being sent right now is left in flight. The server reports each step it did not recreate, with its reason.
 */
export default function ReminderPlanGroup({
  group,
  rows,
  sequenceId,
  timeZone,
  replaced,
  onCancelRow,
  cancellingId,
  onReuse,
}: {
  group: GroupDetail;
  rows: GroupScheduledBroadcast[];
  sequenceId: string;
  /** Closed plans this one replaced (rescheduled), shown collapsed beneath it rather than as peers. */
  replaced?: ReadonlyArray<{ sequenceId: string; rows: GroupScheduledBroadcast[] }>;
  timeZone: string;
  onCancelRow: (id: string) => void;
  cancellingId: string | null;
  /** Passed to each row: "Reuse" on a reminder that may have been sent. */
  onReuse?: (row: GroupScheduledBroadcast) => void;
}) {
  const { cancelSequence, reschedule, readList } = useReminderPlanActions(group.id);
  const [confirm, setConfirm] = useState<'cancel' | 'reschedule' | null>(null);
  const [showReplaced, setShowReplaced] = useState(false);
  const [note, setNote] = useState<{ severity: 'success' | 'warning' | 'error'; text: string } | null>(null);
  // Synchronous guard: a double click during the confirm's close animation lands
  // before `reschedule.isLoading` has re-rendered, and would send the call twice.
  const rescheduling = useRef(false);

  const counts = planCounts(rows);
  const drift = detectPlanDateDrift(rows, group.dateStart, timeZone);
  const dateStart = normalizeTripDate(group.dateStart);
  // The replacement plan's id is DETERMINISTIC (old plan + new start date): two tabs rescheduling the same plan to
  // the same date send the same call, and the API answers the repeat `alreadyExisted`.
  const newSequenceIdForDate = dateStart ? rescheduledSequenceId(sequenceId, dateStart) : sequenceId;
  const plan = useMemo(() => (
    dateStart ? planReschedule({ rows, dateStart, timeZone, newSequenceId: newSequenceIdForDate }) : null
  ), [rows, dateStart, timeZone, newSequenceIdForDate]);
  // A step that would land AFTER the new trip start day is held back until the host says so (the trip day itself is the normal
  // "morning of" reminder and is not held back).
  const [scheduleLate, setScheduleLate] = useState(false);
  const lateSteps = (plan?.requests ?? []).filter((step) => step.lateAfterStart);
  const requestsToSend: PlanRequest[] = useMemo(() => {
    const kept = (plan?.requests ?? []).filter((step) => scheduleLate || !step.lateAfterStart);
    return kept.map((step) => ({ ...step, request: { ...step.request, sequenceTotalSteps: kept.length } }));
  }, [plan, scheduleLate]);

  const cancelPlan = async () => {
    setConfirm(null);
    try {
      const result = await cancelSequence.mutateAsync(sequenceId);
      setNote({ severity: 'success', text: `Plan cancelled: ${describeSequenceCancel(result)}.` });
    } catch (err) {
      setNote({
        severity: 'error',
        text: classifyPlanError(err) === 'unavailable'
          ? "Reminder plans aren't available yet on this server."
          : mutationErrorMessage(err) || 'Could not cancel the plan.',
      });
    }
  };

  const reschedulePlan = async () => {
    if (!dateStart || !plan || requestsToSend.length === 0 || rescheduling.current || reschedule.isLoading) return;
    rescheduling.current = true;
    setConfirm(null);
    try {
      const done = await reschedule.mutateAsync({
        oldSequenceId: sequenceId,
        newSequenceId: newSequenceIdForDate,
        anchorDate: dateStart,
        requests: requestsToSend,
      });
      if (done.rescheduled === false) {
        // Nothing could be recreated, but reminders that may already have been sent WERE cancelled (committed): not a refusal.
        const left = describeExcluded(done.excluded);
        const others = (done.pendingRemaining ?? 0) > 0 ? " The plan's other pending reminders were not part of the request and were left as they are." : '';
        setNote({
          severity: 'warning',
          text: `Nothing was rescheduled: none of the reminders in the request could be recreated. ${done.cancelled} ${done.cancelled === 1 ? 'reminder that may' : 'reminders that may'} already have been sent ${done.cancelled === 1 ? 'was' : 'were'} cancelled, so ${done.cancelled === 1 ? 'it' : 'they'} can't go out again at the old date.${left ? ` ${left}` : ''}${others}`,
        });
      } else if (done.alreadyExisted) {
        // A repeat still reports what the first call left out (a step in doubt must not be forgotten because the answer was lost).
        const left = describeExcluded(done.excluded);
        setNote({
          severity: done.excluded.length > 0 ? 'warning' : 'success',
          text: `This plan was already rescheduled — nothing more to do.${left ? ` ${left}` : ''}`,
        });
      } else {
        // Every pending step of the old plan was cancelled; the ones the server left out of the new plan say why (sent, in doubt, ...).
        const left = describeExcluded(done.excluded);
        setNote({
          severity: done.excluded.length > 0 ? 'warning' : 'success',
          text: `Rescheduled ${plural(done.rows.length, 'reminder')} from the trip's new dates (${plural(done.cancelled, 'old reminder')} cancelled).${left ? ` ${left}` : ''}`,
        });
      }
    } catch (err) {
      const kind = classifyPlanError(err);
      const labels = requestsToSend.map((step) => step.step);
      if (kind === 'plan_changed') {
        // The server changed NOTHING: a reminder it expected to leave out went back to waiting while it ran. Re-read the list so the host sees it.
        await readList().catch(() => null);
        setNote({ severity: 'warning', text: describePlanError(err) });
      } else if (kind === 'unlabelled_steps') {
        await readList().catch(() => null);
        setNote({ severity: 'error', text: describePlanError(err) });
      } else if (kind === 'conflict' || kind === 'cancelled') {
        // The replacement id is derived from the old plan and the date, so this means the reschedule already happened (or was refused
        // for that id) — most likely from another tab. It says nothing about whether anything was changed HERE.
        setNote({ severity: 'warning', text: 'Already rescheduled — possibly in another tab. See the Scheduled sends list below.' });
      } else if (isAfterUnknownOutcome(err) && kind !== 'nothing_to_reschedule') {
        // This answer came from a RETRY of a call whose outcome was unknown: the first one may still be committing, so it proves nothing.
        setNote({
          severity: 'warning',
          text: "We couldn't confirm whether the reschedule went through. Check the Scheduled sends list below; trying again is safe — it can't make a second plan.",
        });
      } else if (kind === 'nothing_to_reschedule') {
        // The server's own sentence: it says whether steps are still pending (the request did not name them) or nothing is left.
        setNote({ severity: 'warning', text: `${describePlanError(err)} The list below is refreshed.` });
      } else if (kind === 'unknown') {
        setNote({
          severity: 'warning',
          text: "We couldn't confirm whether the reschedule went through. Check the Scheduled sends list below; trying again is safe — it can't make a second plan.",
        });
      } else if (kind === 'invalid' || kind === 'unavailable') {
        // A definite refusal before anything was written (one transaction): THIS call changed nothing. But another tab may have rescheduled in
        // the meantime, so say "Nothing was changed" only after re-reading the list and finding the plan as this page saw it.
        const fresh = await readList().catch(() => null);
        const changedMeanwhile = fresh ? planChangedSince(rows, fresh, newSequenceIdForDate) : false;
        const refusal = describePlanError(err, labels);
        if (changedMeanwhile) {
          setNote({ severity: 'warning', text: `${refusal} This plan has changed since this page loaded (another tab rescheduled it, or a reminder was sent). The list below is refreshed.` });
        } else {
          // (If the re-read itself failed we cannot vouch for the list, so the sentence is left out.)
          setNote({ severity: 'error', text: `${refusal}${fresh ? ' Nothing was changed.' : ''}` });
        }
      } else {
        setNote({ severity: 'error', text: describePlanError(err, labels) });
      }
    } finally {
      rescheduling.current = false;
    }
  };

  const busy = cancelSequence.isLoading || reschedule.isLoading;
  const name = planName(rows, timeZone);
  const summary = [
    counts.pending + counts.sending > 0 ? `${counts.pending + counts.sending} pending` : null,
    counts.sent > 0 ? `${counts.sent} sent` : null,
    counts.skipped > 0 ? `${counts.skipped} skipped` : null,
    counts.failed > 0 ? `${counts.failed} failed` : null,
    counts.cancelled > 0 ? `${counts.cancelled} cancelled` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }} data-testid="reminder-plan">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={800}>{name.title}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">{summary}</Typography>
          {name.firstStep ? (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>Starts with “{name.firstStep}”</Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label="Plan" />
          {counts.pending > 0 ? (
            <Button size="small" variant="outlined" color="inherit" disabled={busy} onClick={() => setConfirm('cancel')}>
              {cancelSequence.isLoading ? 'Cancelling…' : 'Cancel whole plan'}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {drift.drifted ? (
        <Alert
          severity="warning"
          sx={{ mt: 1.5 }}
          action={(
            <Button color="inherit" size="small" disabled={busy} onClick={() => setConfirm('reschedule')}>
              Reschedule plan
            </Button>
          )}
        >
          Trip dates changed — these reminders are still set for the old dates.
        </Alert>
      ) : null}
      {note ? <Alert severity={note.severity} sx={{ mt: 1.5 }} onClose={() => setNote(null)}><PlanErrorText text={note.text} /></Alert> : null}
      <Stack spacing={1} sx={{ mt: 1.5 }}>
        {rows.map((row) => (
          <ScheduledRow
            key={row.id}
            nested
            row={row}
            timeZone={timeZone}
            onCancel={onCancelRow}
            cancelling={cancellingId === row.id}
            onReuse={onReuse}
          />
        ))}
      </Stack>

      {replaced && replaced.length > 0 ? (
        <Box sx={{ mt: 1.5 }} data-testid="replaced-plans">
          <Button size="small" color="inherit" onClick={() => setShowReplaced((prev) => !prev)}>
            {showReplaced ? 'Hide' : 'Show'} the earlier {replaced.length === 1 ? 'plan' : 'plans'} this one replaced
            {replacedPlanFlags(replaced) ? ` · ${replacedPlanFlags(replaced)}` : ''}
          </Button>
          <Collapse in={showReplaced} unmountOnExit>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {replaced.map((older) => (
                <Box key={older.sequenceId} sx={{ opacity: 0.8 }} data-testid="replaced-plan">
                  <Typography variant="caption" color="text.secondary" display="block">
                    Replaced by the rescheduled plan · {planName(older.rows, timeZone).title}
                  </Typography>
                  {older.rows.map((row) => (
                    <ScheduledRow key={row.id} nested row={row} timeZone={timeZone} onCancel={onCancelRow} cancelling={cancellingId === row.id} onReuse={onReuse} />
                  ))}
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Box>
      ) : null}

      <Dialog open={confirm === 'cancel'} onClose={() => setConfirm(null)}>
        <DialogTitle>Cancel the whole plan?</DialogTitle>
        <DialogContent>
          <Typography>
            {plural(counts.pending, 'pending reminder')} will be cancelled. Anything
            already sent stays sent.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Keep it</Button>
          <Button variant="contained" color="error" onClick={cancelPlan}>Cancel whole plan</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirm === 'reschedule'} onClose={() => setConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reschedule this plan?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography>
              The pending reminders will be cancelled and the ones listed below created again at the same offsets from the trip&apos;s
              current start date{dateStart ? ` (${tripDateLabel(dateStart)})` : ''}, with the same wording. A reminder marked held back is not re-created,
              and neither is one that may already have been sent (those are cancelled). Reminders already sent, failed, skipped or being sent
              right now are left as they are.
              {plan?.dateChange ? ` The trip date in the copy changes from ${plan.dateChange}.` : ''}
            </Typography>
            {(plan?.requests ?? []).map((step) => (
              <Box key={step.sendAt + step.label} data-testid="reschedule-row">
                <Typography variant="subtitle2" fontWeight={700}>{step.request.subject}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatInZone(step.sendAt, timeZone)} · {audienceKindPhrase(step.audienceKind)}
                  {step.inDoubt ? ' · may already have been sent: cancelled, not re-created' : ''}
                  {step.lateAfterStart && !scheduleLate ? ' · held back: cancelled and not re-created' : ''}
                </Typography>
              </Box>
            ))}
            {lateSteps.length > 0 ? (
              <Alert severity="warning" data-testid="reschedule-late">
                {lateSteps.length} {lateSteps.length === 1 ? 'reminder would' : 'reminders would'} be sent after the trip has started (
                {lateSteps.map((step) => step.label).join(', ')}). {scheduleLate ? 'They will be scheduled.' : 'They are held back: cancelled with the old plan and not re-created.'}
                <FormControlLabel
                  control={<Checkbox size="small" checked={scheduleLate} onChange={(e) => setScheduleLate(e.target.checked)} />}
                  label="Schedule them anyway"
                />
              </Alert>
            ) : null}
            {(plan?.inDoubt ?? []).length > 0 ? (
              <Alert severity="warning" data-testid="reschedule-in-doubt">
                May already have been sent — check before re-sending ({(plan?.inDoubt ?? []).length}):{' '}
                {(plan?.inDoubt ?? []).map((row) => `“${row.subject}”`).join('; ')}.{' '}
                {requestsToSend.length > 0 ? (
                  `They will be cancelled and not re-created. ${RESEND_HINT}`
                ) : (plan?.requests ?? []).length > 0 ? (
                  // Everything left is held back (it would land after the trip starts): the way forward is the checkbox, not the list.
                  'Everything left is held back because it would land after the trip has started. Tick “Schedule them anyway” above to reschedule it; the ones that may have been sent will then be cancelled and not re-created.'
                ) : (
                  // Nothing can be recreated (new time past / wording too long). The sweep retries an attempted reminder on its own.
                  "Nothing else can be rescheduled (their new time is already past or the wording is too long). The system retries a reminder that was attempted on its own within a few minutes, with the same wording (duplicates are suppressed), so leaving it is usually safest. To stop it anyway, cancel it individually from the list below; the row will then offer Reuse."
                )}
              </Alert>
            ) : null}
            {(plan?.dropped ?? []).length > 0 ? (
              <Alert severity="warning">
                Cancelled and not re-created ({(plan?.dropped ?? []).length}):{' '}
                {(plan?.dropped ?? []).map(({ row, reason }) => `“${row.subject}” — ${reason}`).join('; ')}.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Not now</Button>
          <Button
            variant="contained"
            disabled={busy || !plan || requestsToSend.length === 0}
            onClick={reschedulePlan}
          >
            Reschedule plan
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
